import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Loader2, Calendar, User, Tag } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SmoothScroll from "@/components/landing/SmoothScroll";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
}

function buildOgImage(article) {
  if (!article) return undefined;
  const raw = article.cover_image || "";
  if (!raw) return undefined;
  // Base64 data URIs cannot be consumed by social crawlers.
  // Point them to the public cover endpoint which serves the decoded binary.
  if (raw.startsWith("data:")) {
    return `${API}/articles/${article.slug}/cover`;
  }
  return raw;
}

function stripMarkdownLite(text, max = 200) {
  if (!text) return "";
  const cleaned = String(text)
    .replace(/^#{1,6}\s+/gm, "")     // strip heading markers
    .replace(/^>\s+/gm, "")           // strip blockquote markers
    .replace(/\s+/g, " ")
    .trim();
  return cleaned.length > max ? cleaned.slice(0, max - 1).trimEnd() + "…" : cleaned;
}

/** Render article body as clean paragraphs. Also supports `## heading` and blank-line paragraph breaks. */
function ArticleBody({ text }) {
  const blocks = (text || "").replace(/\r\n/g, "\n").split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  return (
    <div className="prose-article space-y-5 text-forest/90 leading-relaxed">
      {blocks.map((b, i) => {
        if (b.startsWith("### ")) {
          return <h3 key={i} className="font-serif-display text-2xl md:text-3xl text-forest mt-6">{b.slice(4).trim()}</h3>;
        }
        if (b.startsWith("## ")) {
          return <h2 key={i} className="font-serif-display text-3xl md:text-4xl text-forest mt-8">{b.slice(3).trim()}</h2>;
        }
        if (b.startsWith("> ")) {
          return (
            <blockquote key={i} className="border-l-4 border-gold pl-5 italic text-moss">
              {b.slice(2).trim()}
            </blockquote>
          );
        }
        return (
          <p key={i} className="text-base md:text-lg leading-[1.85]">
            {b.split("\n").map((line, idx) => (
              <span key={idx}>{line}{idx < b.split("\n").length - 1 ? <br /> : null}</span>
            ))}
          </p>
        );
      })}
    </div>
  );
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios.get(`${API}/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "not-found" : "error"))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <main className="bg-parchment text-forest min-h-screen">
      {article && (
        <Seo
          type="article"
          title={article.title}
          description={
            (article.excerpt && article.excerpt.trim()) ||
            stripMarkdownLite(article.content, 200) ||
            undefined
          }
          image={buildOgImage(article)}
          published={article.published_at || article.created_at}
          modified={article.updated_at}
          author={article.author}
          section={article.category}
          tags={article.tags}
        />
      )}
      {!loading && error === "not-found" && (
        <Seo title="Artikel tidak ditemukan" noindex />
      )}
      <SmoothScroll />
      <Navbar />

      <div className="pt-28 md:pt-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <button
            onClick={() => navigate("/artikel")}
            className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-forest/70 hover:text-gold transition-colors mb-6"
            data-testid="article-back"
          >
            <ArrowLeft size={14} /> Kembali ke daftar
          </button>

          {loading && (
            <div className="py-24 flex items-center justify-center text-moss text-sm">
              <Loader2 className="animate-spin mr-2" size={16} /> Memuat artikel…
            </div>
          )}

          {!loading && error === "not-found" && (
            <div className="py-24 text-center">
              <p className="text-xs tracking-[0.35em] uppercase text-gold">404</p>
              <h1 className="mt-3 font-serif-display text-3xl text-forest">Artikel tidak ditemukan</h1>
              <p className="mt-2 text-moss text-sm">Mungkin sudah dihapus atau ditarik oleh admin.</p>
              <Link
                to="/artikel"
                className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-forest text-parchment text-sm hover:bg-gold transition-colors"
              >
                Lihat artikel lain
              </Link>
            </div>
          )}

          {!loading && error === "error" && (
            <div className="py-24 text-center text-red-700 text-sm">Gagal memuat artikel.</div>
          )}

          {!loading && article && (
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              data-testid="article-detail"
            >
              <p className="text-xs tracking-[0.4em] uppercase text-gold">
                {article.category ? (
                  <Link to={`/artikel?kategori=${encodeURIComponent(article.category)}`} className="hover:underline" data-testid="article-detail-category">
                    Artikel · {article.category}
                  </Link>
                ) : "Artikel"}
              </p>
              <h1
                className="mt-3 font-serif-display text-forest text-4xl md:text-5xl leading-[1.1]"
                data-testid="article-detail-title"
              >
                {article.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-moss">
                {article.published_at && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar size={12} /> {formatDate(article.published_at)}
                  </span>
                )}
                {article.author && (
                  <span className="inline-flex items-center gap-1.5">
                    <User size={12} /> {article.author}
                  </span>
                )}
              </div>

              {article.cover_image && (
                <div className="mt-10 rounded-2xl overflow-hidden border border-gold/30 bg-sand">
                  <img
                    src={article.cover_image}
                    alt={article.title}
                    className="w-full h-auto block"
                    loading="lazy"
                  />
                </div>
              )}

              {article.excerpt && (
                <p className="mt-10 text-lg md:text-xl text-moss italic border-l-4 border-gold pl-5 leading-relaxed">
                  {article.excerpt}
                </p>
              )}

              <div className="mt-10">
                <ArticleBody text={article.content} />
              </div>

              {article.tags && article.tags.length > 0 && (
                <div className="mt-12 pt-6 border-t border-gold/20" data-testid="article-detail-tags">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-moss mb-3">Tag artikel</p>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((t) => (
                      <Link
                        key={t}
                        to={`/artikel?tag=${encodeURIComponent(t)}`}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-sand text-forest border border-gold/30 text-xs hover:bg-gold/20 transition-colors"
                        data-testid={`article-detail-tag-${t}`}
                      >
                        <Tag size={11} /> {t}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-16 pt-8 border-t border-gold/20">
                <Link
                  to="/artikel"
                  className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-forest hover:text-gold transition-colors"
                >
                  <ArrowLeft size={14} /> Semua artikel
                </Link>
              </div>
            </motion.article>
          )}
        </div>
      </div>

      <div className="mt-24">
        <Footer />
      </div>
    </main>
  );
}
